import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SetpasswordForm } from '@/modules/auth/components/set-password';
import { useValidateActivationCodeMutation } from '@/modules/auth/hooks/use-auth';
import { LOGIN_COLORS } from '@/modules/auth/constants/login-theme';
import { useAuthState } from '@/state/client-middleware';
import { useToast } from '@/hooks/use-toast';

export const AccountActivationPage = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const { isMounted, isAuthenticated } = useAuthState();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync: validateActivation, isPending } = useValidateActivationCodeMutation();
  const [isValidCode, setIsValidCode] = useState<boolean | null>(null);

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!code || isAuthenticated) return;

    const validate = async () => {
      try {
        const res = await validateActivation({
          activationCode: code,
          projectKey: import.meta.env.VITE_X_BLOCKS_KEY || '',
        });

        if (res.isSuccess) {
          setIsValidCode(true);
        } else if (res.userId) {
          // Code expired/invalid but we have userId — go to verification-failed so user can resend
          navigate(`/activate-failed?userId=${res.userId}`);
        } else if (res.errors) {
          // No userId available — redirect to signup with error toast
          toast({
            variant: 'destructive',
            title: t('ERROR'),
            description: `${t('ACTIVATION_EXPIRED')}. ${t('PLEASE_TRY_AGAIN')}`,
          });
          navigate('/signup');
        } else {
          toast({
            variant: 'destructive',
            title: t('ERROR'),
            description: `${t('ACTIVATION_EXPIRED')}. ${t('PLEASE_TRY_AGAIN')}`,
          });
          navigate('/signup');
        }
      } catch (error) {
        // Unexpected error — redirect to signup with error toast
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: `${t('ACTIVATION_EXPIRED')}. ${t('PLEASE_TRY_AGAIN')}`,
        });
        navigate('/signup');
      }
    };

    validate();
  }, [code, isAuthenticated, navigate, validateActivation, toast, t]);

  if (!isMounted || isPending || (code && isValidCode === null)) return null;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <h2
          className="text-[2rem] font-bold leading-tight tracking-tight sm:text-[2.125rem]"
          style={{ color: LOGIN_COLORS.textPrimary }}
        >
          {t('COMPLETE_SIGNUP')}
        </h2>
        <div className="flex items-center gap-1">
          <span className="text-sm font-normal leading-5" style={{ color: LOGIN_COLORS.textMuted }}>
            {t('ALREADY_HAVE_ACCOUNT')}
          </span>
          <Link
            to="/login"
            className="text-sm font-semibold leading-5 hover:underline"
            style={{ color: LOGIN_COLORS.primary }}
          >
            {t('LOG_IN')}
          </Link>
        </div>
      </div>
      <SetpasswordForm code={code} />
    </div>
  );
};
